import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import type {
    ImageParamOptions,
    Vehicle,
    VehicleAsset,
} from '~/utils/types/inventory'
import { DayOfTheWeek, Department } from '~/utils/types/location/enums'
import { ASCDepartment } from '~/utils/types/asc/enums'
import { VehicleAssetType } from '~/utils/types/inventory/enums'
import type {
    DaySchemaItem,
    HoursOfOperation,
    HoursOpenClose,
} from '~/utils/types/location'
import type { Facet, FilterOption } from '~/utils/types/filter'

export function convertPhoneToHuman(
    number: string | null,
    friendly: boolean = true,
) {
    if (number === null) {
        return ''
    } else {
        return number
            .replace('+1', '')
            .replace(
                /(\d{3})(\d{3})(\d{4})/,
                friendly ? '($1) $2-$3' : '$1-$2-$3',
            )
    }
}

export function chunkArray(arr: any[], size: number) {
    const returnArr = []

    for (let i = 0; i < arr.length; i += size) {
        returnArr.push(arr.slice(i, i + size))
    }

    return returnArr
}

export function pluralizeWithCount(
    count: number | string,
    noun: string,
    suffix: string = 's',
) {
    if (typeof count === 'string') {
        count = parseInt(count)
    }

    return `${count.toLocaleString()} ${noun}${count !== 1 ? suffix : ''}`
}

export function formatPossessive(word: string) {
    return word.endsWith('s') ? `${word}'` : `${word}'s`
}

export function objectToQuery(obj: any, allowEmptyValues: boolean = false) {
    return Object.keys(obj)
        .filter((key) => allowEmptyValues || obj[key])
        .map((key) => `${key}=${obj[key]}`)
        .join('&')
}

export function queryToObject(str: string) {
    return Object.fromEntries(new URLSearchParams(str))
}

export function formatObjectErrors(errors: any[][] | any[]) {
    const returnObj: any = {}

    Object.keys(errors).forEach((key: any) => {
        returnObj[key] = Array.isArray(errors[key])
            ? errors[key][0]
            : errors[key]
    })

    return returnObj
}

export function getSetting(settings: any, key: string, fallBack: any = null) {
    if (!settings) {
        return null
    }

    const value = key
        .split('.')
        .reduce((a: any, b: any) => (a && a[b] ? a[b] : null), settings)

    return value ?? fallBack
}

export const daysOfTheWeek: DayOfTheWeek[] = [
    DayOfTheWeek.SUNDAY,
    DayOfTheWeek.MONDAY,
    DayOfTheWeek.TUESDAY,
    DayOfTheWeek.WEDNESDAY,
    DayOfTheWeek.THURSDAY,
    DayOfTheWeek.FRIDAY,
    DayOfTheWeek.SATURDAY,
    DayOfTheWeek.SUNDAY,
]

export function currentWeekDay(dayNum: number): string {
    return daysOfTheWeek[dayNum]
}

export function hoursOfOperationForDay(
    weekDay: string | number,
    hoursOfOperation: any,
) {
    if (!hoursOfOperation) {
        return null
    }
    const currentDayOfWeek: string =
        typeof weekDay === 'string' ? weekDay : currentWeekDay(weekDay)

    return hoursOfOperation[currentDayOfWeek]
}

export function groupHoursOfOperation(
    hours: HoursOfOperation,
    shortenDays: boolean = false,
) {
    const { t } = useI18n()

    const daySchema: Record<string, DaySchemaItem> = {
        Monday: {
            name: 'Monday',
            trans: t('monday'),
            short: 'Mon',
            hours: hours.Monday,
        },
        Tuesday: {
            name: 'Tuesday',
            trans: t('tuesday'),
            short: 'Tue',
            hours: hours.Tuesday,
        },
        Wednesday: {
            name: 'Wednesday',
            trans: t('wednesday'),
            short: 'Wed',
            hours: hours.Wednesday,
        },
        Thursday: {
            name: 'Thursday',
            trans: t('thursday'),
            short: 'Thu',
            hours: hours.Thursday,
        },
        Friday: {
            name: 'Friday',
            trans: t('friday'),
            short: 'Fri',
            hours: hours.Friday,
        },
        Saturday: {
            name: 'Saturday',
            trans: t('saturday'),
            short: 'Sat',
            hours: hours.Saturday,
        },
        Sunday: {
            name: 'Sunday',
            trans: t('sunday'),
            short: 'Sun',
            hours: hours.Sunday,
        },
    }

    const returnObj: any = {}

    const keys: string[] = Object.keys(daySchema)
    const groups: any[] = []

    let currentGroup: Record<string, any>[] = []
    let previousValue: HoursOpenClose | null = null

    for (let i = 0; i < keys.length; i++) {
        const day: string = keys[i]
        const schema: DaySchemaItem = daySchema[day]
        if (
            currentGroup.length > 0 &&
            (previousValue?.open !== schema.hours.open ||
                previousValue?.close !== schema.hours.close)
        ) {
            groups.push(currentGroup)
            currentGroup = []
        }

        if (
            currentGroup.length === 0 ||
            (previousValue?.open === schema.hours.open &&
                previousValue?.close === schema.hours.close)
        ) {
            currentGroup.push({
                name: schema.name,
                day: shortenDays ? schema.short : schema.trans,
                hours: schema.hours,
            })

            if (i === keys.length - 1) {
                groups.push(currentGroup)
            }
        }

        previousValue = schema.hours
    }

    // do Sun/Mon closed check
    const firstGroupKey = 0
    const lastGroupKey = groups.length - 1
    const firstGroup = groups[firstGroupKey]
    const lastGroup = groups[lastGroupKey]

    if (firstGroup.length === 1 && lastGroup.length === 1) {
        const firstGroupItem = firstGroup[0]
        const lastGroupItem = lastGroup[0]

        if (
            firstGroupItem.name === 'Monday' &&
            lastGroupItem.name === 'Sunday' &&
            firstGroupItem.hours.open === null &&
            lastGroupItem.hours.open === null
        ) {
            returnObj[`${lastGroupItem.day}-${firstGroupItem.day}`] =
                firstGroupItem.hours

            groups.shift()
            groups.pop()
        }
    }

    // do the rest of the groups
    for (let i = 0; i < groups.length; i++) {
        const group = groups[i]
        const firstItem = group[0]
        const lastItem = group[group.length - 1]

        if (group.length === 1) {
            returnObj[firstItem.day] = firstItem.hours
        } else {
            returnObj[`${firstItem.day}-${lastItem.day}`] = firstItem.hours
        }
    }

    return returnObj
}

export function isDST() {
    const date = new Date()

    const jan = new Date(date.getFullYear(), 0, 1).getTimezoneOffset()
    const jul = new Date(date.getFullYear(), 6, 1).getTimezoneOffset()

    return Math.max(jan, jul) !== date.getTimezoneOffset()
}

export function convertTimezoneToAbbr(timezone: string) {
    const timezoneIndex = isDST() ? 1 : 0

    const timezoneObj: any = {
        'America/Los_Angeles': ['PST', 'PDT'],
        'America/Phoenix': ['MST'],
        'America/Boise': ['MST', 'MDT'],
        'America/Chicago': ['CST', 'CDT'],
        'America/New_York': ['EST', 'EDT'],
    }

    return timezoneObj[timezone]
        ? timezoneObj[timezone].length === 1
            ? timezoneObj[timezone][0]
            : timezoneObj[timezone][timezoneIndex]
        : null
}

export function ucwords(str: string): string {
    return (str + '')
        .toLowerCase()
        .replace(/^([a-z])|\s+([a-z])/g, (s) => s.toUpperCase())
}

export function formatSearchTerm(searchTerm: string) {
    searchTerm = searchTerm.replace('_', ', ')

    if (searchTerm.includes(' > ')) {
        searchTerm = searchTerm.split(' > ')[1]
    }

    return searchTerm
}

export function stripWhitespace(string: string) {
    const regex = /(\([^)]*\))|(\s+)/g

    return string.replace(regex, (group1) => group1 || '')
}

export function inventoryImageUrl(image: string, settings: ImageParamOptions) {
    if (!settings) {
        return image
    }

    const paramArr = []

    if (settings.maxWidth) {
        paramArr.push(`mw_${settings.maxWidth}`)
    } else if (settings.width) {
        paramArr.push(`w_${settings.width}`)
    }

    if (settings.height) {
        paramArr.push(`h_${settings.height}`)
    }

    if (settings.format) {
        paramArr.push(`format_${settings.format}`)
    }

    if (settings.overlay) {
        paramArr.push(settings.overlay)
    }

    if (settings.fit) {
        paramArr.push(`fit_${settings.fit}`)
    }

    if (settings.position) {
        paramArr.push(`pos_${settings.position}`)
    }

    if (paramArr.length === 0) {
        return image
    }

    const start: string = image.substring(0, image.lastIndexOf('/') + 1)
    const paramStr: string = paramArr.join(',')
    const end: string = image.substring(
        image.lastIndexOf('/') + 1,
        image.length,
    )

    return `${start}${paramStr}/${end}`
}

export function formatMaskedValue(value: string | number): number {
    return parseInt(value.toString().replace(/,/g, ''))
}

export function fractionToDecimal(string: string) {
    let total: number = 0
    const numSplit: string[] = string.split(' ')

    for (let i: number = 0; i < numSplit.length; i++) {
        const numberStr: string = numSplit[i]

        if (numberStr.includes('/')) {
            const fractionSplit: string[] = numberStr.split('/')
            total += parseInt(fractionSplit[0]) / parseInt(fractionSplit[1])
        } else {
            total += parseInt(numberStr)
        }
    }

    return total
}

export function addServerValidationErrors(
    form$: any,
    errors: Record<string, string[]>,
): void {
    Object.keys(errors).forEach((key: string): void => {
        form$.value.el$(key)?.messageBag.append(errors[key][0])
    })
}

export function clearServerValidationErrors(form$: any): void {
    Object.keys(form$.value.data).forEach((key: string): void => {
        form$.value.el$(key)?.messageBag.clear()
    })
}

export function formatRangeValues(
    value: string,
    separator: string = '-',
): Record<'min' | 'max', number> {
    const splitArr: string[] = value.split(separator)

    return {
        min: parseInt(splitArr[0]),
        max: parseInt(splitArr[1]),
    }
}

export function formatThousands(num: number, withDecimal: boolean = true) {
    const roundNum = Math.round((num / 1000) * 10) / 10
    return num < 1000 ? num : `${withDecimal ? roundNum : roundNum.toFixed(0)}K`
}

export function blokId(blok: Record<string, any>) {
    return `dm-blok-${blok._uid}`
}

export function randomString(length: number) {
    const characters: string =
        'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
    let result: string = ' '
    const charactersLength: number = characters.length

    for (let i: number = 0; i < length; i++) {
        result += characters.charAt(
            Math.floor(Math.random() * charactersLength),
        )
    }

    return result
}

export function formatTimeWithMinuteCheck(time: string): string {
    const timeObj: Dayjs = dayjs(time, 'HH:mm')
    return timeObj.format(timeObj.minute() === 0 ? 'hA' : 'h:mmA')
}

export function parseLocaleTemplates(
    message: string,
    schema: Record<string, any>,
): string {
    Object.keys(schema).forEach((search: string) => {
        message = message.replaceAll(`[${search}]`, schema[search])
    })

    return message
}

export function getMainVehicleMuxVideoAsset(vehicle: Vehicle) {
    if (!vehicle.assets) {
        return null
    }

    const assets = vehicle.assets.filter(
        (asset: VehicleAsset) =>
            asset.type === VehicleAssetType.VIDEO &&
            asset.active &&
            asset.display &&
            asset.mux_url !== null,
    )

    return assets.length > 0 ? assets[0] : null
}

export function convertLocationDepartmentToASCDepartment(
    locationDepartment: string | Department | null,
): ASCDepartment {
    if (!locationDepartment) {
        return ASCDepartment.UNKNOWN
    }

    let ascDepartment: ASCDepartment

    switch (locationDepartment) {
        case Department.SALES:
            ascDepartment = ASCDepartment.SALES
            break
        case Department.SERVICE:
            ascDepartment = ASCDepartment.SERVICE
            break
        case Department.PARTS:
            ascDepartment = ASCDepartment.PARTS
            break
        case Department.DETAIL:
            ascDepartment = ASCDepartment.BODY_SHOP
            break
        default:
            ascDepartment = ASCDepartment.UNKNOWN
    }

    return ascDepartment
}

export function vehicleNameFromVehicle(
    vehicle: Vehicle,
    withTrim: boolean = true,
): string {
    let returnStr = `${vehicle.year} ${vehicle.make} ${vehicle.model}`

    if (withTrim && vehicle.trim) {
        returnStr += ` ${vehicle.trim}`
    }

    return returnStr
}

export function removeEmptyValuesFromObject(
    obj: Record<any, any>,
): Record<any, any> {
    return Object.fromEntries(Object.entries(obj).filter(([_, v]) => v))
}

export function formatMiles(miles: number) {
    return miles < 1000 ? miles : Math.round((miles / 1000) * 10) / 10 + 'k'
}

export function numberFormat(
    amount: string | number | undefined | null,
    roundType: 'round' | 'ceil' | 'floor' | null = null,
) {
    if (!amount) {
        return amount
    }

    amount = parseInt(amount.toString())

    switch (roundType) {
        case 'round':
            amount = Math.round(amount)
            break
        case 'ceil':
            amount = Math.ceil(amount)
            break
        case 'floor':
            amount = Math.floor(amount)
            break
    }

    return new Intl.NumberFormat().format(amount)
}

export function beginsWithVowel(word: string): boolean {
    return /^[aeiou]/i.test(word)
}

export function toLocaleFixed(
    number: number,
    maxDigit: number | undefined = undefined,
    minDigit: number | undefined = undefined,
) {
    return number.toLocaleString(undefined, {
        minimumFractionDigits: minDigit,
        maximumFractionDigits: maxDigit,
    })
}

export function plural(word: string, amount?: number): string {
    if (amount !== undefined && amount === 1) {
        return word
    }

    const plural: { [key: string]: string } = {
        '(quiz)$': '$1zes',
        '^(ox)$': '$1en',
        '([m|l])ouse$': '$1ice',
        '(matr|vert|ind)ix|ex$': '$1ices',
        '(x|ch|ss|sh)$': '$1es',
        '([^aeiouy]|qu)y$': '$1ies',
        '(hive)$': '$1s',
        '(?:([^f])fe|([lr])f)$': '$1$2ves',
        '(shea|lea|loa|thie)f$': '$1ves',
        sis$: 'ses',
        '([ti])um$': '$1a',
        '(tomat|potat|ech|her|vet)o$': '$1oes',
        '(bu)s$': '$1ses',
        '(alias)$': '$1es',
        '(octop)us$': '$1i',
        '(ax|test)is$': '$1es',
        '(us)$': '$1es',
        '([^s]+)$': '$1s',
    }
    const irregular: { [key: string]: string } = {
        move: 'moves',
        foot: 'feet',
        goose: 'geese',
        sex: 'sexes',
        child: 'children',
        man: 'men',
        tooth: 'teeth',
        person: 'people',
    }
    const uncountable: string[] = [
        'sheep',
        'fish',
        'deer',
        'moose',
        'series',
        'species',
        'money',
        'rice',
        'information',
        'equipment',
        'bison',
        'cod',
        'offspring',
        'pike',
        'salmon',
        'shrimp',
        'swine',
        'trout',
        'aircraft',
        'hovercraft',
        'spacecraft',
        'sugar',
        'tuna',
        'you',
        'wood',
    ]

    // save some time in the case that singular and plural are the same
    if (uncountable.includes(word.toLowerCase())) {
        return word
    }

    // check for irregular forms
    for (const w in irregular) {
        const pattern = new RegExp(`${w}$`, 'i')
        const replace = irregular[w]
        if (pattern.test(word)) {
            return word.replace(pattern, replace)
        }
    }

    // check for matches using regular expressions
    for (const reg in plural) {
        const pattern = new RegExp(reg, 'i')
        if (pattern.test(word)) {
            return word.replace(pattern, plural[reg])
        }
    }

    return word
}

export function normalizeFacets(
    data: Record<string, any>,
    query: Record<string, any>,
): Record<string, Facet[]> {
    const returnObj: Record<string, any> = {}
    const parentChildFacets: string[] = [
        'make_model.lvl1',
        'location_search_term.lvl1',
        'bed_length_group.lvl1',
        'body_style_extended.lvl1',
    ]

    Object.keys(data).forEach((facet: string) => {
        const facetData = data[facet]
        const options: any[] = []

        Object.keys(facetData).forEach((value: string) => {
            const count = facetData[value]

            // important: make the check case-insensitive
            const valueArr: string[] = query[facet]
                ? query[facet].toLowerCase().split(',')
                : []

            const optionObj: Facet = {
                value,
                count,
                checked: valueArr.includes(value.toLowerCase()),
            }

            if (parentChildFacets.includes(facet)) {
                const facetSplit: string[] = value.split(' > ')

                optionObj.parent = facetSplit[0]
                optionObj.child = facetSplit[1]
            }

            options.push(optionObj)
        })

        returnObj[facet] = options
    })

    return returnObj
}

export function recursivelyResetFacets(obj: any) {
    for (const key in obj) {
        if (key === 'checked' && obj[key]) {
            obj[key] = false
        } else if (typeof obj[key] === 'object') {
            recursivelyResetFacets(obj[key])
        }
    }
}

export function generateFilters(obj: Record<string, any>): Record<string, any> {
    const params: Record<string, any> = {}

    Object.keys(obj).forEach((key: string) => {
        Object.assign(params, { [`filters[${key}]`]: obj[key] })
    })

    return params
}

export function filterHiddenFacets(
    filters: any[],
    hiddenFacets: string[],
): FilterOption[] {
    return hiddenFacets.length > 0
        ? filters.filter((filter: any) => !hiddenFacets.includes(filter.id))
        : filters
}
